import { useEffect, useState } from "react";
import { getDeviceKey, setDeviceKey, setSiteId, syncRoster, rosterStatus, clearRoster, waitForRosterIdle } from "./roster";

/* DEVICE SETUP — admin-facing modal (English only, not a worker surface).
   The tablet is tied to a device API key issued by the org dashboard; the
   key lives in this browser's localStorage. Saving triggers a roster sync:
   worker photos are downloaded, face-embedded locally and cached in
   IndexedDB so identification works fast (and offline-ish) at the kiosk. */
export default function DeviceSetup({ onClose }) {
  const [key, setKey] = useState(getDeviceKey());
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [msg, setMsg] = useState("");

  const refresh = () => rosterStatus().then(setStatus).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const doSync = async (force) => {
    if (busy) return;
    const k = key.trim();
    if (!k) { setMsg("Enter the device API key first."); return; }
    setBusy(true); setMsg(""); setProgress(null);
    try {
      await waitForRosterIdle();
      // Key rotation is an identity boundary: another org's cached photos and
      // face embeddings must never survive — wipe and re-download everything.
      const prev = getDeviceKey();
      // Any key change is an identity boundary — including the FIRST key on a
      // kiosk that previously ran in keyless site mode (its site roster and
      // sync cursor must not mix into the keyed roster). Site selection is
      // dropped too: the key now defines the site.
      if (prev !== k) { await clearRoster(); setSiteId(""); force = true; }
      setDeviceKey(k);
      const r = await syncRoster({ force, onProgress: setProgress });
      setMsg(`Synced — ${r.total} workers on device, ${r.updated} updated, ${r.embedded} face-embedded${r.noFace ? `, ${r.noFace} without a usable photo` : ""}.`);
    } catch (e) {
      setMsg(`Sync failed: ${e?.message || e}`);
    } finally {
      setBusy(false); setProgress(null); refresh();
    }
  };

  const clearKey = async () => {
    await waitForRosterIdle();
    try { await clearRoster(); } catch (_) {}
    setDeviceKey(""); setKey("");
    setMsg("Device unlinked — roster cache cleared, kiosk runs in standalone mode.");
    refresh();
  };

  return (
    <div className="oh-devmodal-backdrop" onClick={onClose}>
      <div className="oh-devmodal" onClick={(e) => e.stopPropagation()}>
        <h3>Device setup</h3>
        <p className="oh-devmodal-sub">Link this tablet to the organisation dashboard with its device API key. Worker photos are cached and face-matched on this device.</p>
        <label className="oh-field oh-field-wide">
          <span>Device API key</span>
          <input type="text" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" spellCheck={false} placeholder="Paste the key issued for this device" />
        </label>
        {status && (
          <p className="oh-devmodal-status">
            Roster on device: <b>{status.total}</b> workers · <b>{status.embedded}</b> face-ready
            {status.lastSync ? <> · last sync {new Date(status.lastSync).toLocaleString()}</> : " · never synced"}
          </p>
        )}
        {progress && <p className="oh-devmodal-status">Embedding photos… {progress.done}/{progress.total}</p>}
        {msg && <p className="oh-devmodal-msg">{msg}</p>}
        <div className="oh-devmodal-actions">
          <button className="oh-btn oh-btn-sm" disabled={busy} onClick={() => doSync(false)}>{busy ? "Syncing…" : "Save & sync"}</button>
          <button className="oh-btn oh-btn-sm ghost" disabled={busy} onClick={() => doSync(true)}>Full resync</button>
          {getDeviceKey() && <button className="oh-btn oh-btn-sm ghost" disabled={busy} onClick={clearKey}>Unlink</button>}
          <button className="oh-btn oh-btn-sm ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
